import { live, LiveController, RubySymbol, State, Task } from "@camertron/live-component";

type GameComponentProps = {
  board: {
    segments: Array<[[number, number], RubySymbol, number]>
    status: RubySymbol
  }
}

@live("Snake::GameComponent")
export class GameComponent extends LiveController<GameComponentProps> {
  private interval_id: number;
  private abort_controller: AbortController | null = null;
  private keydown_fn: (event: KeyboardEvent) => void;

  connect() {
    super.connect();

    this.reset_timer();

    const { signal } = (this.abort_controller = new AbortController())
    const body = document.querySelector("body");

    if (this.keydown_fn) {
      body?.removeEventListener("keydown", this.keydown_fn);
    }

    this.keydown_fn = (event: KeyboardEvent) => {
      switch (event.key) {
        case "ArrowDown":
          this.turn_down();
          break;
        case "ArrowUp":
          this.turn_up();
          break;
        case "ArrowLeft":
          this.turn_left();
          break;
        case "ArrowRight":
          this.turn_right();
          break;
        default:
          // return to avoid preventing default
          return;
      }

      event.preventDefault(); // disallow scrolling via arrow keys
    }

    body?.addEventListener("keydown", this.keydown_fn, { signal });
  }

  reset_timer() {
    this.stop_timer();
    this.interval_id = setInterval(() => this.tick(), 900) as unknown as number;
  }

  stop_timer() {
    if (!this.interval_id) return;
    if (this.interval_id) clearInterval(this.interval_id);
  }

  disconnect() {
    this.abort_controller?.abort();

    document.querySelector("body").removeEventListener("keydown", this.keydown_fn);
    this.keydown_fn = null;

    this.stop_timer();
  }

  tick() {
    this.render((component) => {
      component.call("move");
    });
  }

  before_update(state: State<GameComponentProps>) {
    if (!this.state) return;

    if (state.props.board.status.value == "game_over") {
      this.stop_timer();
    }
  }

  turn_down() {
    this.turn("down");
  }

  turn_up() {
    this.turn("up");
  }

  turn_left() {
    this.turn("left");
  }

  turn_right() {
    this.turn("right");
  }

  turn(dir: string) {
    if (!this.can_turn(dir)) return;

    this.stop_timer();

    this.render({ mode: "preempt" }, (component) => {
      component.call("turn", { dir })
    }).then(() => {
      this.reset_timer();
    });
  }

  private can_turn(next_direction: string): boolean {
    const current_direction = this.props.board.segments[0][1].value;
    const opposites = (
      (next_direction === "up" && current_direction === "down") ||
      (next_direction === "down" && current_direction === "up") ||
      (next_direction === "left" && current_direction === "right") ||
      (next_direction === "right" && current_direction === "left")
    );

    return !opposites;
  }

  new_game() {
    this.render((component) => {
      component.call("new_game");
    }).then(() => {
      this.reset_timer();
    })
  }
}
